"use client"

import * as React from "react"
import { Link, usePage } from "@inertiajs/react"
import { Inbox as InboxIcon, MapPin, Users, BarChart3, BookOpen, Settings, ChevronRight } from "lucide-react"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from "@/components/ui/shadcn/sidebar"
import { AccountSwitcher } from "@/components/Layout/AccountSwitcher"
import { AvailabilityStatusToggle } from "@/components/Layout/AvailabilityStatusToggle"
import { CountBadge } from "@/components/ui/CountBadge"
import { WidgetSetupPrompt, useWidgetConfigured } from "@/components/WidgetSetupPrompt"
import { useRouteParams } from "@/hooks/useRoutes"
import routes from "@/routeHelpers"
import type { Account, Inbox } from "@/types/shared"

interface SharedData {
  current_account: Account | null
  accounts: Account[]
  inboxes: Inbox[]
  active_visitors_count: number
  contacts_count: number
}

export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const { url, props: pageProps } = usePage<{ props: SharedData }>()
  const { current_account, accounts, inboxes, active_visitors_count, contacts_count } = pageProps as unknown as SharedData
  const { withAccount, withInbox } = useRouteParams()
  const widgetConfigured = useWidgetConfigured()
  const [inboxExpanded, setInboxExpanded] = React.useState(true)

  // Determine active section from URL
  const isInboxActive = url.includes('/inbox') || url.includes('/scream')
  const isVisitorsActive = url.includes('/visitors')
  const isContactsActive = url.includes('/contacts')
  const isAnalyticsActive = url.includes('/analytics')
  const isHelpActive = url.includes('/help/')
  const isSettingsActive = url.includes('/settings') && !isHelpActive

  // Get current inbox ID from URL if present
  const currentInboxId = React.useMemo(() => {
    const match = url.match(/\/inbox\/([^/]+)/)
    return match ? match[1] : null
  }, [url])

  const totalOpenCount = inboxes.reduce((sum, inbox) => sum + inbox.open_count, 0)
  const totalUnreadCount = inboxes.reduce((sum, inbox) => sum + inbox.unread_count, 0)

  return (
    <Sidebar
      collapsible="icon"
      className="border-r"
      {...props}
    >
      <SidebarHeader>
        <AccountSwitcher
          currentAccount={current_account}
          accounts={accounts || []}
        />
        {!widgetConfigured && (
          <div className="px-2 pt-2 group-data-[collapsible=icon]:hidden">
            <WidgetSetupPrompt variant="compact" />
          </div>
        )}
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent className="px-1.5 md:px-0">
            <SidebarMenu>
              {/* Inbox with collapsible sub-items */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip={{ children: "Inbox", hidden: false }}
                  isActive={isInboxActive && !currentInboxId}
                  className="px-2.5 md:px-2 cursor-pointer"
                  onClick={() => setInboxExpanded(!inboxExpanded)}
                >
                  <InboxIcon />
                  <span className="flex-1">Inbox</span>
                  {totalUnreadCount > 0 ? (
                    <CountBadge count={totalUnreadCount} className="ml-auto group-data-[collapsible=icon]:hidden" />
                  ) : totalOpenCount > 0 && (
                    <CountBadge count={totalOpenCount} variant="muted" className="group-data-[collapsible=icon]:hidden" />
                  )}
                  {inboxes.length > 1 && (
                    <ChevronRight
                      className={`size-4 transition-transform group-data-[collapsible=icon]:hidden ${inboxExpanded ? 'rotate-90' : ''}`}
                    />
                  )}
                </SidebarMenuButton>

                {/* Sub-menu for individual inboxes */}
                {inboxExpanded && inboxes.length > 0 && (
                  <SidebarMenuSub className="group-data-[collapsible=icon]:hidden">
                    {/* All Inboxes option */}
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={isInboxActive && !currentInboxId}
                        className="cursor-pointer"
                      >
                        <Link href={routes.inbox.show.path(withAccount())}>
                          <span>All Inboxes</span>
                          {totalUnreadCount > 0 ? (
                            <CountBadge count={totalUnreadCount} className="ml-auto" />
                          ) : totalOpenCount > 0 && (
                            <CountBadge count={totalOpenCount} variant="muted" className="ml-auto" />
                          )}
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>

                    {/* Individual inboxes */}
                    {inboxes.map((inbox) => (
                      <SidebarMenuSubItem key={inbox.id}>
                        <SidebarMenuSubButton
                          asChild
                          isActive={currentInboxId === inbox.id}
                          className="cursor-pointer"
                        >
                          <Link href={routes.inbox.show.path(withInbox({ inbox_id: inbox.id }))}>
                            <span>{inbox.name}</span>
                            {inbox.unread_count > 0 ? (
                              <CountBadge count={inbox.unread_count} className="ml-auto" />
                            ) : inbox.open_count > 0 && (
                              <CountBadge count={inbox.open_count} variant="muted" className="ml-auto" />
                            )}
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>

              {/* Visitors */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip={{ children: "Visitors", hidden: false }}
                  isActive={isVisitorsActive}
                  className="px-2.5 md:px-2 cursor-pointer"
                  asChild
                >
                  <Link href={routes.visitors.show.path(withAccount())}>
                    <MapPin />
                    <span>Visitors</span>
                    <CountBadge count={active_visitors_count} className="ml-auto group-data-[collapsible=icon]:hidden" />
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Contacts */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip={{ children: "Contacts", hidden: false }}
                  isActive={isContactsActive}
                  className="px-2.5 md:px-2 cursor-pointer"
                  asChild
                >
                  <Link href={routes.contacts.index.path(withAccount())}>
                    <Users />
                    <span>Contacts</span>
                    <CountBadge count={contacts_count} variant="muted" className="ml-auto group-data-[collapsible=icon]:hidden" />
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Analytics */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip={{ children: "Analytics", hidden: false }}
                  isActive={isAnalyticsActive}
                  className="px-2.5 md:px-2 cursor-pointer"
                  asChild
                >
                  <Link href={routes.analytics.show.path(withAccount())}>
                    <BarChart3 />
                    <span>Analytics</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Help center */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip={{ children: "Help center", hidden: false }}
                  isActive={isHelpActive}
                  className="px-2.5 md:px-2 cursor-pointer"
                  asChild
                >
                  <Link href={routes.helpArticles.index.path(withAccount())}>
                    <BookOpen />
                    <span>Help center</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Settings */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip={{ children: "Settings", hidden: false }}
                  isActive={isSettingsActive}
                  className="px-2.5 md:px-2 cursor-pointer"
                  asChild
                >
                  <Link href={routes.settings.show.path(withAccount())}>
                    <Settings />
                    <span>Settings</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="gap-2">
        <AvailabilityStatusToggle />
      </SidebarFooter>
    </Sidebar>
  )
}

export default AppSidebar
